import { TFunction } from 'i18next';
import { IPricingDto } from '../../API/Interfaces/Locker/ILockerSizeDto';
import { DictUserPanelDisplayPrice, PriceTypeLabel } from '../../Shared';
import { IAddress } from '../../Shared/Interfaces/IAdress';
import { AddressFormatType } from '../../Shared/Types/AddressFormatType';
import { isValidNumber, parsePhoneNumber } from 'libphonenumber-js';
import { APP_CONFIG } from '../../API/config';

import moment from 'moment';

import { formatDecimal, formatShortDate, groupThousands } from '../Format/localeFormat';

export const EmailRegex = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/u;

export type BillingPeriodUnit = '1M' | '2W' | '1W';
export type RentElements = '#offices' | '#parkings' | '#lockers' | '#services';
export const getPrices = (userPanelDisplayPrice: DictUserPanelDisplayPrice, pricing?: IPricingDto) => {
	const isGross = userPanelDisplayPrice === DictUserPanelDisplayPrice.Gross;

	return {
		company: isGross ? pricing?.companyGross : pricing?.companyNet,
		private: isGross ? pricing?.privateGross : pricing?.privateNet,
	};
};

export const getPrice = (
	isCompany: boolean,
	userPanelDisplayPrice: DictUserPanelDisplayPrice,
	pricing: IPricingDto
) => {
	const prices = getPrices(userPanelDisplayPrice, pricing);
	return (isCompany ? prices.company : prices.private) ?? 0;
};

export const getPriceLabel = (
	t: TFunction,
	options: {
		isGross: boolean;
		perMonth?: boolean;
	}
) => {
	let label = t(`common.price.${options.isGross ? 'gross' : 'net'}`);
	if (options.perMonth) return label + ' ' + t('common.perMonth');
	else return label;
};

export const netOrGrossBasedOnIndex = (index: 1 | 2, isGross: boolean) => {
	switch (index) {
		case 1:
			return isGross ? PriceTypeLabel.Gross : PriceTypeLabel.Net;
		case 2:
			return isGross ? PriceTypeLabel.Net : PriceTypeLabel.Gross;
	}
};

export enum DictErrorCode {
	BadRequest = 400,
	NotFound = 40400,
	EmailInUse = 40002,
	LockerNotAvailable = 40028,
	TokenAndTmpPasswordExpired = 40048,
	PhoneNumberInUse = 40062,
	NotEnoughLockers = 40064,
	UserHasLockers = 40067,
	NipNumberInUse = 40068,
	NoCommunicationWithGate = 40092,
	NoPermissionsToSendCommand = 40351,
	GateNotFound = 40424,
	LockerNotFound = 40404,
	InternalError = 50000,
	NoInternetConnection = 50001,
	CodeGenerationNoPossible = 40012,
	NoCommunicationWithLocker = 40011,
	LocationHasNoDevice = 40010,
	IncorrectPassword = 40050,
	UserAlreadyExist = 400005,
	ContractNotExists = 40414,
	TokenNotExists = 40436,
	InvalidAdminToken = 40521,
	InvalidUserToken = 40522,
	EmailDosntMatch = 400023,
	TenantRentCompanyAndPrivate = 400041,
	UserToFarAway = 40369,
	UpgradeRequired = 42600,
	EmailIsNotVerified = 40372,
	ExternalLoginEmailNotVerified = 40375,
	EmailAlreadyVerified = 400074,
	OAuthDidNotReturnEmail = 40377,
	AlarmZoneNotFound = 40637,
	AlarmZoneCodeNotFound = 40640,
	TooManyRequests = 42900,
}

export enum ResponseStatus {
	BadRequest = 400,
	Unauthorized = 401,
	Forbidden = 403,
	NotFound = 404,
	MethodNotAllowed = 405,
	UpgradeRequired = 426,
	TooManyRequests = 429,
	InternalServerError = 500,
}

/**
 * Builds the standard API error envelope. Used when normalising a response whose body cannot be
 * trusted - a 500 from a gateway or a rejected 429 may carry an HTML page or nothing at all, and
 * assigning onto such a body in place throws instead of producing an error the UI can translate.
 */
export const buildErrorEnvelope = (errorCode: DictErrorCode | number) => ({
	success: false,
	errors: [{ errorCode, message: errorCode.toString() }],
});

/** Seconds to wait before retrying, taken from the Retry-After header when the server sent one. */
export const parseRetryAfterSeconds = (headers: Record<string, unknown> | undefined, fallbackSeconds: number) => {
	const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
	const seconds = Number(raw);

	return Number.isFinite(seconds) && seconds > 0 ? seconds : fallbackSeconds;
};

export const includedInCurrentPath = (url: string) => {
	return location.pathname.toLocaleLowerCase().includes(url.toLocaleLowerCase());
};

export const formatAddress = (address: IAddress, type: AddressFormatType, noLoacationWrap?: boolean): string => {
	if (type === 'short') {
		return `${address.city}, ${address.street} ${address.streetNumber}`;
	} else if (type === 'long') {
		return address.flatNumber
			? `${address.street} ${address.streetNumber}/${address.flatNumber}${noLoacationWrap ? ' ' : '\n'}${address.postCode} ${address.city}`
			: `${address.street} ${address.streetNumber}${noLoacationWrap ? ' ' : '\n'}${address.postCode} ${address.city}`;
	}
	if (type === 'street') {
		return `${address.street} ${address.streetNumber}${(address.flatNumber && address.streetNumber && '/' + address.flatNumber) || ''}`;
	}
	if (type === 'postalAddress') {
		return `${address.postCode} ${address.city}`;
	} else {
		return `${address.city},  ${address.street} ${address.streetNumber}${address.flatNumber != null ? '/' + address.flatNumber : ''}`;
	}
};

// Chains with the displayCurrencySymbol feature turned on show the currency symbol
// instead of the ISO code. Without the flag every currency below falls back to its code.
const currencySymbolMap = new Map<string, string>([['CZK', 'Kč']]);

const displayCurrencySymbolEnabled = (): boolean => {
	const featuresConfig = getStorageData(storageKeys.SP_USER_PANEL_FEATURES_CONFIG, 'local') as {
		displayCurrencySymbol?: boolean;
	} | null;

	return !!featuresConfig?.displayCurrencySymbol;
};

const getCurrencyLabelMap = (t: (key: string) => string) => {
	const currencyLabelMap = new Map<string, string>([
		['PLN', t('common.currency.pln')],
		['USD', '$'],
	]);

	if (displayCurrencySymbolEnabled()) {
		currencySymbolMap.forEach((symbol, currencyCode) => currencyLabelMap.set(currencyCode, symbol));
	}

	return currencyLabelMap;
};

export const getCurrencyLabel = (currency: string | undefined, t: (key: string) => string): string => {
	const currencyLabelMap = getCurrencyLabelMap(t);
	return currency ? (currencyLabelMap.get(currency) ?? currency) : '';
};

export const getBillingPeriodLabel = (
	t: TFunction,
	unit: BillingPeriodUnit,
	count: number,
	includeCount = true
): string => {
	if (unit === '2W') {
		const weeks = count * 2;
		return includeCount ? `${weeks} ${t('common.weeksShort')}` : t('common.weeksShort');
	}

	if (unit === '1W') {
		if (count === 1) {
			return includeCount ? `${count} ${t('common.oneWeekShort')}` : t('common.oneWeekShort');
		}
		return includeCount ? `${count} ${t('common.weeksShort')}` : t('common.weeksShort');
	}

	return includeCount ? `${count} ${t('common.monthShort', { count })}` : t('common.monthShort');
};

export function numberWithSpaces(x: number | string) {
	return groupThousands(x);
}

export const parseAndFormatPrice = (
	price: number | undefined,
	decimalPoints: number = 2,
	convertToMainCurrency: boolean = true
) => {
	const parsedPrice = Number(price);
	if (!isNaN(parsedPrice)) return formatDecimal(parsedPrice / (convertToMainCurrency ? 100 : 1), decimalPoints);
	return '';
};

export enum DictDocumentType {
	IDCard = 1,
	Passport = 2,
	DrivingLicense = 3,
	PersonalIdNumber = 4,
	EsDNI = 5,
	EsNIE = 6,
	DateOfBirth = 7,
}

/** Fields for each document type - single source of truth */
export const DOCUMENT_FIELDS_CONFIG: Record<DictDocumentType, string[]> = {
	[DictDocumentType.IDCard]: ['identityCardNumber', 'pesel'],
	[DictDocumentType.Passport]: ['passportNumber', 'dateOfBirth'],
	[DictDocumentType.DrivingLicense]: ['driverLicenseNumber'],
	[DictDocumentType.PersonalIdNumber]: ['pesel'],
	[DictDocumentType.EsDNI]: ['esDNI'],
	[DictDocumentType.EsNIE]: ['esNIE'],
	[DictDocumentType.DateOfBirth]: ['dateOfBirth'],
};

/** All sensitive data field names */
export const ALL_SENSITIVE_DATA_FIELDS = [
	'identityCardNumber',
	'pesel',
	'passportNumber',
	'dateOfBirth',
	'driverLicenseNumber',
	'esDNI',
	'esNIE',
	'grAFM',
	'grDOY',
	'foreignTaxNumber',
] as const;

export function getDateOfBirthFromPESEL(pesel: string): string {
	if (!/^\d{11}$/.test(pesel)) return '';

	const yearPart = parseInt(pesel.slice(0, 2), 10);
	const monthPart = parseInt(pesel.slice(2, 4), 10);
	const day = parseInt(pesel.slice(4, 6), 10);

	let year = 1900 + yearPart;
	let month = monthPart;

	if (monthPart >= 81 && monthPart <= 92) {
		year = 1800 + yearPart;
		month = monthPart - 80;
	} else if (monthPart >= 1 && monthPart <= 12) {
		year = 1900 + yearPart;
	} else if (monthPart >= 21 && monthPart <= 32) {
		year = 2000 + yearPart;
		month = monthPart - 20;
	} else if (monthPart >= 41 && monthPart <= 52) {
		year = 2100 + yearPart;
		month = monthPart - 40;
	} else if (monthPart >= 61 && monthPart <= 72) {
		year = 2200 + yearPart;
		month = monthPart - 60;
	} else {
		return '';
	}

	const date = new Date(year, month - 1, day);
	return formatShortDate(date);
}

export function getFormattedPhoneNumber(phone?: string, phoneCountryCode?: string) {
	if (!phone || !phoneCountryCode) return '';
	const phoneNumber = `+${phoneCountryCode}${phone}`;
	const formattedPhoneNumber = isValidNumber(phoneNumber)
		? parsePhoneNumber(phoneNumber)?.formatInternational()
		: phoneNumber;
	return formattedPhoneNumber;
}

export enum DictChainIntegrationType {
	OnlyContactDetails = 1,
	ContactDetailsAndPriceList = 2,
	ContactDetailsAndPriceListAndAvailibility = 3,
	FullAdminPanelWithCommonUserPanel = 4,
	FullAdminPanelWithoutCommonUserPanel = 5,
}

export const storageKeys = {
	SP_ACCESS_TOKEN: 'sp_access_token',
	SP_REFRESH_TOKEN: 'sp_refresh_token',
	SP_USER_NAME: 'sp_user_name',
	SP_LAST_REQ: 'sp_last_req',
	SP_ORDER_ITEM_TYPES: 'sp_order_item_types',
	SP_SELECTED_ORDER_ITEM_TYPE: 'sp_selected_order_item_type',
	SP_INSURANCES_DATA: 'sp_insurances_data',
	SP_EXTENSIONS_DATA: 'sp_extensions_data',
	SP_LOCATION_CURRENCY_CODE: 'sp_location_currency_code',
	SP_BILLING_PERIOD_UNITS: 'sp_billing_period_units',
	SP_SELECTED_LOCATION: 'sp_selected_location',
	SP_RENT_FORM: 'sp_rent_form',
	SP_CONFIG_CHAIN_ID: 'sp_config_chain_id',
	SP_CONFIG_NAVBAR_LOGO_REDIRECT_URL: 'sp_config_navbar_logo_redirect_url',
	SP_CONFIG_FOOTER_LOGO_REDIRECT_URL: 'sp_config_footer_logo_redirect_url',
	SP_ACCESS_MODAL_INVOKED: 'sp_access_modal_invoked',
	SP_CONFIG_THEME_JSON: 'sp_config_theme_json',
	SP_LOCATION_TRANSLATIONS: 'sp_location_translations',
	SP_ORDER_MANAGEMENT_TOKEN: 'sp_order_management_token',
	SP_CHAIN_TRANSLATIONS: 'sp_chain_translations',
	SP_SESSION_ID: 'sp_session_id',
	SP_CREATE_ORDER_SESSION_ID: 'sp_create_order_session_id',
	SP_BROWSER_LANGUAGE: 'sp_browser_language',
	SP_I18NEXT_LNG: 'sp_i18next_lng',
	SP_I18NEXT_SUPPORTED_LNGS: 'sp_i18next_supported_lngs',
	SP_COOKIE_ACCEPT: 'sp_cookie_accept',
	SP_MONITORING_USER_ACTIVITY: 'sp_monitoring_user_activity',
	SP_USER_PANEL_VERSION: 'sp_user_panel_version',
	SP_HAS_SINGLE_DEFAULT_PRICE_LIST: 'sp_has_single_default_price_list',
	SP_ADDED_CARD_TOAST: 'sp_added_card_toast',
	SP_CHANGED_CARD_TOAST: 'sp_changed_card_toast',
	SP_USER_HAS_ORDERS: 'sp_user_has_orders',
	SP_USER_HAS_ASSIGNED_ACCESS: 'sp_user_has_assigned_access',
	SP_GTM_PURCHASE: 'sp_gtm_purchase',
	SP_FIREBASE_CONFIG: 'sp_firebase_config',
	SP_USER_CONSENTS: 'sp_user_consents',
	SP_DEVICE_ID: 'sp_device_id',
	SP_USER_PANEL_FEATURES_CONFIG: 'sp_user_panel_features_config',
} as const;

type StorageKey = (typeof storageKeys)[keyof typeof storageKeys];
/**
 * Retrieves data from localStorage or sessionStorage for a given key.
 * If the value is JSON-encoded, it will be parsed automatically.
 *
 * @param key - A valid key from the `storageKeys` object.
 * @param type - The type of storage to use: `'local'` for localStorage or `'session'` for sessionStorage.
 * @returns The parsed value (if JSON), a raw string, or `null` if the key doesn't exist.
 */
export const getStorageData = (key: StorageKey, type: 'local' | 'session') => {
	let data = null;
	let storageDataString = type === 'local' ? localStorage.getItem(key) : sessionStorage.getItem(key);
	if (storageDataString) {
		try {
			data = JSON.parse(storageDataString);
		} catch (error) {
			data = storageDataString;
		}
	}
	return data;
};

export const isDefinedAndNotNull = <T>(value: T) => {
	return value !== undefined && value !== null;
};

export const formatAccessCode = (code?: string | number | null, suffix?: string | null): string => {
	if (code == null) return '';

	const normalizedCode = String(code);
	const spacedCode = [...normalizedCode].join(' ');
	return suffix ? `${spacedCode} ${suffix}` : spacedCode;
};

const updateFavicon = (url: string) => {
	let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;

	if (!link) {
		link = document.createElement('link');
		link.rel = 'icon';
		document.head.appendChild(link);
	}

	link.href = url;
};

export const setFaviconFromBlob = async (chainId: string) => {
	const faviconUrl = `${APP_CONFIG.blobUrl}/assets/chains/${chainId}/favicon.ico`;
	const defaultFaviconUrl = '/favicon_default.ico';

	try {
		const response = await fetch(faviconUrl, { method: 'HEAD' });

		if (!response.ok) {
			throw new Error('Favicon not found');
		}

		updateFavicon(faviconUrl);
	} catch {
		updateFavicon(defaultFaviconUrl);
	}
};

export const convertToPOJO = <T>(proxyObject: T): T => JSON.parse(JSON.stringify(proxyObject)) as T;

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;

export const getUnitOfTimeByBillingPeriodUnit = (billingPeriodUnit?: BillingPeriodUnit) =>
	billingPeriodUnit === '1M' ? 'months' : 'weeks';

export const getRatioByBillingPeriodUnit = (billingPeriodUnit?: BillingPeriodUnit) =>
	billingPeriodUnit === '2W' ? 2 : 1;

export const rentalLengthCount = (
	startDate: string | Date | null,
	endDate: string | Date | null,
	periodUnit: BillingPeriodUnit
): number => {
	if (!endDate) return 0;

	return Math.floor(
		moment(endDate).add(1, 'days').diff(startDate, getUnitOfTimeByBillingPeriodUnit(periodUnit)) /
			getRatioByBillingPeriodUnit(periodUnit)
	);
};

export const emailInputProps = {
	inputMode: 'email',
	autoCapitalize: 'none',
	autoCorrect: 'off',
	spellCheck: false,
} as const;
